import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
  Favorite as FavoriteIcon,
  Star as StarIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  Home as HomeIcon,
  Category as CategoryIcon
} from '@mui/icons-material';

interface ThemePreviewProps {
  themeId: string;
  themeName: string;
  open: boolean;
  onClose: () => void;
  onApply?: (themeId: string) => void;
}

const ThemePreview: React.FC<ThemePreviewProps> = ({
  themeId,
  themeName,
  open,
  onClose,
  onApply
}) => {
  const [previewApplied, setPreviewApplied] = useState(false);

  const handlePreviewTheme = () => {
    if (typeof window !== 'undefined' && (window as any).applyStoreTheme) {
      (window as any).applyStoreTheme(themeId);
      setPreviewApplied(true);
    }
  };

  const handleApplyTheme = () => {
    if (onApply) {
      onApply(themeId);
    }
    onClose();
  };

  const handleClose = () => {
    // إعادة تطبيق الثيم الأصلي عند الإغلاق
    if (previewApplied && typeof window !== 'undefined') {
      // يمكن إضافة منطق لإعادة الثيم الأصلي هنا
    }
    setPreviewApplied(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '80vh',
          borderRadius: 2
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">
            معاينة الثيم: {themeName}
          </Typography>
          <IconButton onClick={handleClose} size="large">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" color="text.secondary" paragraph>
            هذه معاينة مباشرة لكيفية ظهور المتجر الإلكتروني بالثيم المختار
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Button
              variant="outlined"
              startIcon={<VisibilityIcon />}
              onClick={handlePreviewTheme}
              disabled={previewApplied}
            >
              {previewApplied ? 'تم تطبيق المعاينة' : 'معاينة الثيم'}
            </Button>
            
            {previewApplied && (
              <Chip 
                label="معاينة مطبقة" 
                color="success" 
                variant="outlined" 
              />
            )}
          </Box>
        </Box>

        {/* محاكاة رأس المتجر */}
        <Paper sx={{ p: 3, mb: 3 }} className="store-header">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" color="inherit">
              🛍️ اليكترونيات السواح
            </Typography>
            <Box display="flex" gap={2}>
              <Button color="inherit" startIcon={<HomeIcon />}>
                الرئيسية
              </Button>
              <Button color="inherit" startIcon={<CategoryIcon />}>
                المنتجات
              </Button>
              <Button color="inherit" startIcon={<ShoppingCartIcon />}>
                السلة (3)
              </Button>
            </Box>
          </Box>
        </Paper>

        <Grid container spacing={3}>
          {/* منطقة المنتجات */}
          <Grid item xs={12} md={8}>
            <Typography variant="h6" gutterBottom>
              المنتجات المميزة
            </Typography>
            
            <Grid container spacing={2}>
              {[1, 2, 3, 4].map((item) => (
                <Grid item xs={12} sm={6} key={item}>
                  <Card className="product-card">
                    <CardMedia
                      component="img"
                      height="140"
                      image={`https://source.unsplash.com/300x200?product=${item}&sig=${themeId}`}
                      alt={`منتج ${item}`}
                    />
                    <CardContent>
                      <Typography gutterBottom variant="h6" component="div">
                        منتج رائع {item}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        وصف مختصر للمنتج مع أهم المميزات والخصائص
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1} sx={{ mt: 1 }}>
                        <Box display="flex">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <StarIcon 
                              key={star} 
                              sx={{ 
                                fontSize: 16, 
                                color: star <= 4 ? 'gold' : 'grey.300' 
                              }} 
                            />
                          ))}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          (24 تقييم)
                        </Typography>
                      </Box>
                      <Typography variant="h6" color="primary" sx={{ mt: 2 }}>
                        {(item * 150).toLocaleString('ar-EG')} ج.م
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<ShoppingCartIcon />}
                        fullWidth
                      >
                        أضف للسلة
                      </Button>
                      <IconButton size="small" color="secondary">
                        <FavoriteIcon />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* الشريط الجانبي */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                التصنيفات
              </Typography>
              <List dense>
                {[
                  'الإلكترونيات',
                  'الملابس والأزياء',
                  'المنزل والحديقة',
                  'الرياضة واللياقة',
                  'الكتب والمجلات'
                ].map((category, index) => (
                  <ListItem key={category} button>
                    <ListItemIcon>
                      <CategoryIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={category} />
                  </ListItem>
                ))}
              </List>
            </Paper>

            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                العروض الخاصة
              </Typography>
              <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: 1, mb: 2 }}>
                <Typography variant="h6">خصم 25%</Typography>
                <Typography variant="body2">على جميع المنتجات الإلكترونية</Typography>
              </Box>
              <Box sx={{ p: 2, bgcolor: 'secondary.main', color: 'secondary.contrastText', borderRadius: 1 }}>
                <Typography variant="h6">شحن مجاني</Typography>
                <Typography variant="body2">للطلبات أكثر من 500 ج.م</Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* تذييل المتجر */}
        <Paper sx={{ p: 3, mt: 3 }} className="store-footer">
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom>
                معلومات الاتصال
              </Typography>
              <Typography variant="body2">
                📧 info@alsawah-electronics.com<br />
                📞 0123456789<br />
                📍 العنوان، المدينة، البلد
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom>
                روابط سريعة
              </Typography>
              <Typography variant="body2">
                • سياسة الخصوصية<br />
                • شروط الاستخدام<br />
                • الدعم الفني
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom>
                تابعنا
              </Typography>
              <Typography variant="body2">
                📱 فيسبوك | تويتر | إنستغرام
              </Typography>
            </Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" align="center">
            © {new Date().getFullYear()} المتجر الإلكتروني. جميع الحقوق محفوظة.
          </Typography>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose} color="inherit">
          إلغاء
        </Button>
        <Button 
          onClick={handlePreviewTheme} 
          variant="outlined" 
          disabled={previewApplied}
        >
          معاينة
        </Button>
        <Button 
          onClick={handleApplyTheme} 
          variant="contained" 
          color="primary"
        >
          تطبيق الثيم
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ThemePreview;
